import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { sortAlphabetically } from '../../../utils';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';

@Component({
  selector: 'mwo-smart-grid',
  templateUrl: './smart-grid.component.html',
  styleUrls: ['./smart-grid.component.scss', './grid.scss', '../../../../../node_modules/keen-slider/keen-slider.min.css'],
  encapsulation: ViewEncapsulation.None,
})
export class SmartGridComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('sliderRef') sliderRef: ElementRef<HTMLElement> | undefined;
  @Input() list: Observable<any[]> = of(null);
  @Input() itemTpl: TemplateRef<any>;
  @Input() searchSubjects: string[] = ['name'];
  @Input() fullPage = true;
  @Input() hasSettings = false;
  @Input() hasSearch = true;
  @Input() mode = 'grid';
  @Input() mini = false;
  @Input() isSearchPage = false;
  @Output() onRowClick: EventEmitter<string> = new EventEmitter();
  isLoading$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  smartlist$: Observable<any[]>;
  @Input() sortFn = sortAlphabetically();
  displayedItems: any[] | undefined;
  slider: KeenSliderInstance | null = null;
  pages: any[] = [];
  currentSlide = 0;
  dotHelper: Array<number> = [];
  private destroy$ = new Subject();
  private readonly _itemsPerPage = 4;
  doneLoading = false;

  constructor(private router: Router) {
  }

  @Input() filterFn: (e: object, i: number) => boolean = (e, i) => true;

  ngOnInit() {
    this.doneLoading = false;
    this.smartlist$ = this.list.pipe(
      startWith([]),
      switchMap(list => of(list)),
      tap(() => this.isLoading$.next(false)),
      takeUntil(this.destroy$)
    );

    this.smartlist$.subscribe(items => {
      this.displayedItems = items;
      this.fillLastPage();
      this.pages = new Array(Math.ceil((this.displayedItems?.length ? this.displayedItems.length : 0) / this._itemsPerPage));
      this.loadSlider();
    });
  }

  ngAfterViewInit(): void {
    if (this.sliderRef) {
      this.loadSlider();
    }
  }

  private loadSlider() {
    setTimeout(() => {
      if (!this.sliderRef) {
        return;
      }
      this.slider = new KeenSlider(this.sliderRef.nativeElement, {
        loop: false,
        mode: 'snap',
        renderMode: 'performance',
        slides: {
          perView: 1,
          spacing: 0,
        },
        vertical: false,
        initial: this.currentSlide,
        slideChanged: (s) => {
          this.currentSlide = s.track.details.rel;
        }
      });
      this.dotHelper = [
        ...Array(this.slider.track.details.slides.length).keys(),
      ];
      this.doneLoading = true;
    });
  }

  handleSearch(searchFn) {
    this.list = searchFn(this.list, this.searchSubjects);
  }

  trackBy(item: { id: string, path: string }) {
    return item.id || item.path;
  }

  goToPath(path) {
    this.router.navigate([path]);
  }

  fillLastPage() {
    if (this.displayedItems && this.displayedItems.length !== 0) {
      const remainder = this.displayedItems.length % this._itemsPerPage;
      if (remainder !== 0) {
        const objectsToAdd = this._itemsPerPage - remainder;
        const emptyItems: any[] = Array(objectsToAdd).fill({ id: '_', name: '_', placeholder: true });
        this.displayedItems = this.displayedItems.concat(emptyItems);
      }
    }
  }

  ngOnDestroy() {
    if (this.slider) {
      this.slider.destroy();
    }

    this.destroy$.next(undefined);
    this.destroy$.complete();
  }
}
